<script lang="ts" setup>
import { computed, onMounted, watch } from 'vue'
import { useTolgee } from '@tolgee/vue'
import { languages, LS_LANGUAGE_KEY } from '@/plugins/tolgee'
import { usePublicData } from '@/stores/PublicData'
import { LANGUAGES, type LanguageType } from '@/api/interfaces'
import {
  getLocale,
  includes,
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
} from '@/helpers/utils'
import { useRoute } from 'vue-router'

const tolgee = useTolgee(['language', 'pendingLanguage'])
const store = usePublicData()

const route = useRoute()
const companyId: string = route.params.id as string

const switchLanguage = () => {
  const html = document.querySelector('html')

  if (html) {
    html.setAttribute('lang', store.lang)
  }

  safeLocalStorageSetItem(LS_LANGUAGE_KEY, store.lang)

  tolgee.value.changeLanguage(getLocale(store.lang))

  store.fetchPublicData(companyId, store.lang)
}

const applyInitialLanguage = (language: LanguageType) => {
  if (store.lang === language) {
    switchLanguage()
    return
  }

  store.lang = language
}

const languageSelectorItems = computed(() => {
  return LANGUAGES.map((lang) => ({ title: languages[lang], value: lang })).sort((a, b) =>
    a.title.localeCompare(b.title),
  )
})

watch(
  () => store.lang,
  () => {
    switchLanguage()
  },
)

onMounted(() => {
  const fallbackLocale = import.meta.env.VITE_FALLBACK_LOCALE
  const systemLanguage = window.navigator.language.substring(0, 2)
  const persistedLanguage = safeLocalStorageGetItem(LS_LANGUAGE_KEY) || fallbackLocale

  if (LANGUAGES.includes(persistedLanguage)) {
    applyInitialLanguage(persistedLanguage)
    return
  }

  if (includes(LANGUAGES, systemLanguage)) {
    applyInitialLanguage(systemLanguage)
    return
  }

  applyInitialLanguage(fallbackLocale)
})
</script>

<template>
  <v-select
    v-model="store.lang"
    :items="languageSelectorItems"
    class="language-selector"
    density="compact"
    hide-details
    variant="outlined"
  ></v-select>
</template>

<style lang="scss" scoped>
.language-selector {
  color: var(--ui-black);

  .dark & {
    color: var(--ui-white);
  }
}
</style>
